// Textos de las acciones de estado: confirmación, éxito y error.
import { ApiError } from '@/api/errors';
import type {
    RetailPromotionsErrorCode,
    SalePromotion,
    SalePromotionTransition,
} from '../api/promotionsContract';
import { retailerScopeErrorCopy } from './retailerScopeError';

interface ActionCopy {
    /** Infinitivo para «No se pudo … la promo». */
    verb: string;
    done: string;
    confirm?: {
        title: (name: string) => string;
        description: string;
        confirmLabel: string;
        destructive: boolean;
    };
}

export const ACTION_COPY: Record<SalePromotionTransition, ActionCopy> = {
    PUBLISH: {
        verb: 'publicar',
        done: 'Promo publicada',
        confirm: {
            title: (name) => `¿Publicar «${name}»?`,
            description:
                'Se empieza a ofrecer en las ventas nuevas desde el inicio de su vigencia. Después puedes pausarla o vencerla.',
            confirmLabel: 'Publicar',
            destructive: false,
        },
    },
    PAUSE: { verb: 'pausar', done: 'Promo pausada' },
    RESUME: { verb: 'reanudar', done: 'Promo reanudada' },
    EXPIRE: {
        verb: 'vencer',
        done: 'Promo vencida',
        confirm: {
            title: (name) => `¿Vencer «${name}» ahora?`,
            description:
                'Su fin pasa a este momento y ya no se ofrece en ninguna venta nueva. Las ventas que ya la cotizaron la conservan. No se puede deshacer.',
            confirmLabel: 'Vencer ahora',
            destructive: true,
        },
    },
    ARCHIVE: {
        verb: 'archivar',
        done: 'Promo archivada',
        confirm: {
            title: (name) => `¿Archivar «${name}»?`,
            description:
                'Deja de verse en la lista y ya no aplica. Sus ventas y su historial se conservan; la encuentras en el filtro Archivadas.',
            confirmLabel: 'Archivar',
            destructive: true,
        },
    },
};

const INVALID_TRANSITION =
    'La promo cambió de estado mientras la veías. Actualizamos la lista; revisa su estado e intenta de nuevo.';

const SYSTEM_TRANSITION_NOT_ALLOWED =
    'El bono de bienvenida solo se puede pausar o reanudar; no se publica, se vence ni se archiva.';

const STATUS_ERROR_MESSAGES: Partial<Record<RetailPromotionsErrorCode, string>> = {
    INVALID_PROMOTION_TRANSITION: INVALID_TRANSITION,
    SYSTEM_PROMOTION_TRANSITION_NOT_ALLOWED: SYSTEM_TRANSITION_NOT_ALLOWED,
};

export function statusChangeErrorDescription(error: unknown): string {
    const retailerMessage = retailerScopeErrorCopy(error);
    if (retailerMessage) return retailerMessage;
    if (error instanceof ApiError) {
        const known = STATUS_ERROR_MESSAGES[error.code as RetailPromotionsErrorCode];
        if (known) return known;
    }
    if (error instanceof Error) return error.message;
    return 'Intenta de nuevo en un momento.';
}

/** Toast de éxito al guardar: dice en qué estado quedó la promo. */
export function saveSuccessCopy(
    promo: Pick<SalePromotion, 'name' | 'status' | 'version'>,
    isNew: boolean,
): { title: string; description: string } {
    if (!isNew)
        return {
            title: 'Versión nueva guardada',
            description: `${promo.name} · versión ${String(promo.version)}`,
        };
    return {
        title: promo.status === 'DRAFT' ? 'Promo creada como borrador' : 'Promo publicada',
        description: promo.name,
    };
}
